/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { GridLayoutItem } from '@app/core/types';
import { GRID_LAYOUT_MIN_COLS, GRID_LAYOUT_VISIBLE_ROWS } from '@common/components/widgets-dashboard/constants';

type Partition = number[];

/**
 * Generates all possible partitions of a given number.
 * @param {number} count - The number to partition.
 * @returns {Generator<Partition>} A generator that yields each partition as an array of numbers.
 * @example
 * // To get all partitions of the number 4:
 * const partitions = generatePartitions(4);
 * for (const partition of partitions) {
 *   console.log(partition);
 * }
 * // Output:
 * // [4]
 * // [3, 1]
 * // [2, 2]
 * // [2, 1, 1]
 * // [1, 1, 1, 1]
 */
function* generatePartitions(count: number): Generator<Partition> {
  const currentPartition: Partition = new Array(count + 1).fill(0);
  // Current index in the partition array
  let partitionIndex = 1;
  currentPartition[1] = count;

  while (partitionIndex !== 0) {
    const nextValue = (currentPartition[partitionIndex - 1] ?? 0) + 1;
    let remainingValue = (currentPartition[partitionIndex] ?? 0) - 1;

    partitionIndex--;

    while (nextValue <= remainingValue) {
      currentPartition[partitionIndex] = nextValue;
      remainingValue -= nextValue;
      partitionIndex++;
    }

    currentPartition[partitionIndex] = nextValue + remainingValue;

    yield currentPartition.slice(0, partitionIndex + 1);
  }
}

/**
 * Calculates the squareness of a grid layout.
 * The squareness is defined as the sum of the squares of the differences
 * between the width and height of each grid item.
 *
 * @param {GridLayoutItem[]} layouts - An array of grid layout items.
 * @returns {number} The sum of the squares of the differences between the width and height of each grid item.
 *
 * @example
 * const layouts = [
 *   { w: 2, h: 3 },
 *   { w: 4, h: 4 },
 *   { w: 5, h: 3 },
 * ];
 * const result = squareness(layouts);
 * console.log(result); // Output: 5 ( (2-3)^2 + (4-4)^2 + (5-3)^2 )
 */
const squareness = (layouts: GridLayoutItem[]): number => {
  let squaresSum: number = 0.0;
  for (const c of layouts) {
    squaresSum += Math.pow(c.w - c.h, 2);
  }
  return squaresSum;
};

/**
 * Converts a partition into a grid layout with specified width and height.
 * Each number in the partition represents the number of columns in that row.
 *
 * @param {Partition} partition - An array of numbers representing the partition.
 * @param {number} width - The total width of the layout.
 * @param {number} height - The total height of the layout.
 * @returns {GridLayoutItem[]} An array of grid layout items with specified positions and dimensions.
 *
 * @example
 * const partition = [2, 3];
 * const width = 6;
 * const height = 6;
 * const layout = toLayout(partition, width, height);
 * console.log(layout);
 * // Output:
 * // [
 * //   { x: 0, y: 0, w: 3, h: 3 },
 * //   { x: 3, y: 0, w: 3, h: 3 },
 * //   { x: 0, y: 3, w: 2, h: 3 },
 * //   { x: 2, y: 3, w: 2, h: 3 },
 * //   { x: 4, y: 3, w: 2, h: 3 }
 * // ]
 */
const toLayout = (partition: Partition, width: number, height: number): GridLayoutItem[] => {
  const layouts: GridLayoutItem[] = [];
  const rows: number = partition.length;
  const h: number = Math.round(height / rows); // height per row

  for (let j = 0; j < rows; ++j) {
    const cols: number = partition[j] ?? 0;
    const w: number = Math.round(width / cols); // width per col

    for (let i = 0; i < cols; ++i) {
      layouts.push({ x: i * w, y: j * h, w, h });
    }
  }

  return layouts;
};

/**
 * Generates a grid layout with the least squareness for a given number of items and pages.
 *
 * @param {number} count - The number of items to layout.
 * @param {number} [pages=1] - The number of pages to span the layout across.
 * @returns {GridLayoutItem[]} An array of grid layout items with specified positions and dimensions.
 *
 * @example
 * const count = 6;
 * const pages = 2;
 * const layout = generateLayout(count, pages);
 * console.log(layout);
 *
 * // Output:
 * // [
 * //   {x: 0, y: 0, w: 6, h: 8},
 * //   {x: 6, y: 0, w: 6, h: 8},
 * //   {x: 0, y: 8, w: 6, h: 8},
 * //   {x: 6, y: 8, w: 6, h: 8},
 * //   {x: 0, y: 16, w: 6, h: 8},
 * //   {x: 6, y: 16, w: 6, h: 8}
 * // ]
 */
export const generateLayout = (count: number, pages: number = 1): GridLayoutItem[] => {
  const partitions = generatePartitions(count);
  let min: number = Number.MAX_VALUE;
  let best: GridLayoutItem[] = [];
  while (true) {
    let next = partitions.next();
    if (!next.done) {
      const layout = toLayout(next.value, GRID_LAYOUT_MIN_COLS, GRID_LAYOUT_VISIBLE_ROWS * pages);
      const square = squareness(layout);
      if (square < min) {
        min = square;
        best = layout;
      }
    } else {
      break;
    }
  }
  return best;
};
